import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Sparkles, AlertTriangle, CheckCircle2, ArrowLeft, Wand2 } from "lucide-react";

const EXAMPLES = [
    "Need 3 senior Python engineers in Mumbai (full-time, hybrid) and 1 product designer in Bengaluru.",
    "Hiring: DevOps engineer (AWS, Terraform), Full-stack engineer (React/Node), and an SDET. All remote.",
    "5 sales executives for our Delhi office. Mid-level. Hybrid. Immediate joiner.",
];

export default function BulkJobCreate() {
    const nav = useNavigate();
    const [prompt, setPrompt] = useState("");
    const [plan, setPlan] = useState(null);
    const [busy, setBusy] = useState(false);
    const [creating, setCreating] = useState(false);

    const analyze = async () => {
        if (!prompt.trim()) return toast.error("Paste your hiring brief first");
        setBusy(true);
        try {
            const { data } = await api.post("/jobs/bulk-create", { prompt });
            setPlan(data);
            if (!data.plan || data.plan.length === 0) {
                toast.warning("Couldn't extract any roles. Try a clearer brief.");
            } else {
                toast.success(`Extracted ${data.plan.length} role${data.plan.length === 1 ? "" : "s"}`);
            }
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setBusy(false); }
    };

    const createAll = async () => {
        if (!plan?.plan?.length) return;
        const newOnes = plan.plan.filter((p) => !p.duplicates?.length).length;
        if (newOnes === 0) return toast.error("Every role looks like a duplicate. Edit your brief or proceed individually.");
        if (!window.confirm(`Create ${newOnes} draft job(s)? Duplicates will be skipped.`)) return;
        setCreating(true);
        try {
            const { data } = await api.post("/jobs/bulk-create", { prompt, auto_create: true });
            toast.success(`Created ${data.created?.length || 0} drafts · ${data.duplicates?.length || 0} skipped as duplicates`);
            if (data.created?.length) {
                setTimeout(() => nav("/jobs?status=draft"), 1200);
            } else {
                setPlan(data);
            }
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setCreating(false); }
    };

    return (
        <><div className="max-w-[1600px] mx-auto px-3 sm:px-4 py-3 sm:py-5 space-y-6">
                <div className="flex items-end justify-between gap-4 flex-wrap">
                    <div>
                        <div className="text-xs uppercase tracking-widest font-mono text-ikb">Bulk import</div>
                        <h1 className="font-display font-black text-2xl sm:text-4xl mt-2">Create multiple jobs at once</h1>
                        <p className="text-sm text-muted-foreground mt-2">Paste a hiring brief or a list of roles. AI splits it into structured drafts and flags duplicates.</p>
                    </div>
                    <Button variant="outline" className="rounded-none h-10" onClick={() => nav("/jobs")} data-testid="bulk-back">
                        <ArrowLeft className="w-4 h-4 mr-2" /> Back to jobs
                    </Button>
                </div>

                <div className="border border-border bg-white p-4 sm:p-6 space-y-3">
                    <label className="text-xs uppercase tracking-widest font-mono text-foreground/80">Hiring brief</label>
                    <textarea
                        value={prompt}
                        onChange={(e) => setPrompt(e.target.value)}
                        rows={8}
                        className="w-full border border-border p-3 text-sm rounded-none font-mono"
                        placeholder="Paste a list of roles, or describe what you're hiring in plain English…"
                        data-testid="bulk-prompt"
                    />
                    <div className="flex items-center justify-between gap-3 flex-wrap">
                        <div className="text-[11px] text-muted-foreground">
                            <span className="font-mono uppercase tracking-widest">Try:</span>{" "}
                            {EXAMPLES.map((ex, i) => (
                                <button key={i} type="button" onClick={() => setPrompt(ex)} className="underline text-ikb mx-1.5" data-testid={`bulk-example-${i}`}>
                                    Example {i + 1}
                                </button>
                            ))}
                        </div>
                        <Button onClick={analyze} disabled={busy} className="rounded-none bg-ikb hover:bg-[#001f7a] text-white h-10" data-testid="bulk-analyze">
                            <Sparkles className="w-4 h-4 mr-2" />
                            {busy ? "Analyzing…" : "Analyze with AI"}
                        </Button>
                    </div>
                </div>

                {plan?.plan?.length > 0 && (
                    <div className="space-y-3" data-testid="bulk-plan">
                        <div className="flex items-end justify-between flex-wrap gap-2">
                            <h2 className="font-display font-bold text-xl">Extracted roles ({plan.plan.length})</h2>
                            <Button onClick={createAll} disabled={creating} className="rounded-none bg-ikb hover:bg-[#001f7a] text-white h-10" data-testid="bulk-create-all">
                                <Wand2 className="w-4 h-4 mr-2" />
                                {creating ? "Creating drafts…" : "Create all as drafts →"}
                            </Button>
                        </div>
                        {plan.plan.map((p, i) => (
                            <div key={i} className="border border-border bg-white p-4" data-testid={`bulk-plan-item-${i}`}>
                                <div className="flex items-start justify-between gap-3 flex-wrap">
                                    <div>
                                        <h3 className="font-display font-bold text-base">{p.title}</h3>
                                        <div className="text-xs text-foreground/70 mt-1 font-mono flex flex-wrap gap-3">
                                            <span>{p.seniority}</span>
                                            <span>·</span>
                                            <span>{p.experience_min}–{p.experience_max} yrs</span>
                                            <span>·</span>
                                            <span>{p.employment_type}</span>
                                            <span>·</span>
                                            <span>{p.work_mode}</span>
                                            {p.location && (<><span>·</span><span>{p.location}</span></>)}
                                            {p.department && (<><span>·</span><span>{p.department}</span></>)}
                                            {p.headcount > 1 && (<><span>·</span><span className="text-ikb font-bold">×{p.headcount} openings</span></>)}
                                        </div>
                                    </div>
                                    {p.duplicates?.length > 0 ? (
                                        <span className="inline-flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-widest border border-amber-300 bg-amber-50 text-amber-800 px-2 py-1" data-testid={`bulk-dup-flag-${i}`}>
                                            <AlertTriangle className="w-3 h-3" /> Possible duplicate
                                        </span>
                                    ) : (
                                        <span className="inline-flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-widest border border-emerald-300 bg-emerald-50 text-emerald-800 px-2 py-1">
                                            <CheckCircle2 className="w-3 h-3" /> Ready
                                        </span>
                                    )}
                                </div>
                                {p.duplicates?.length > 0 && (
                                    <div className="mt-2 border-l-2 border-amber-300 pl-3">
                                        <div className="text-[10px] font-mono uppercase tracking-widest text-amber-800 mb-1">Similar active jobs</div>
                                        <ul className="space-y-1">
                                            {p.duplicates.map((d, j) => (
                                                <li key={j} className="text-xs">
                                                    <a href={`/jobs/${d.id}`} target="_blank" rel="noreferrer" className="text-ikb hover:underline">{d.title}</a>
                                                    {" · "}<span className="text-foreground/70 font-mono">{Math.round(d.similarity * 100)}% match</span>
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </>);
}
